import React, { useState, useEffect } from 'react';
import Navbar from '../components/common/Navbar';
import Alert from '../components/common/Alert';
import AdminForm from '../components/admin/AdminForm';
import TeacherForm from '../components/admin/TeacherForm';
import ModerationProfileForm from '../components/admin/ModerationProfileForm';
import PromptForm from '../components/admin/PromptForm';
import { adminAPI } from '../services/api';
import { useAlertContext } from '../context/AlertContext';

const AdminPage = () => {
	const [activeTab, setActiveTab] = useState('admins');
	const [admins, setAdmins] = useState([]);
	const [teachers, setTeachers] = useState([]);
	const [moderationProfiles, setModerationProfiles] = useState([]);
	const [prompts, setPrompts] = useState([]);
	const [editingItem, setEditingItem] = useState(null);
	const [models, setModels] = useState([]);
	const { showAlert } = useAlertContext();

	useEffect(() => {
		fetchData();
		fetchModels();
	}, []);

	const fetchModels = async () => {
		const response = await adminAPI.getModelList();
		if (response.models) {
			setModels(response.models);
		} else if (response.error) {
			showAlert(response.error, 'danger');
		}
	};

	const fetchData = async () => {
		try {
			// Load data for all tabs
			const adminsResponse = await adminAPI.getAdmins();
			if (!adminsResponse.error) {
				setAdmins(adminsResponse);
			}

			const teachersResponse = await adminAPI.getTeachers();
			if (!teachersResponse.error) {
				setTeachers(teachersResponse);
			}

			const profilesResponse = await adminAPI.getModerationProfiles();
			if (!profilesResponse.error) {
				setModerationProfiles(profilesResponse);
			}

			const promptsResponse = await adminAPI.getPrompts();
			if (!promptsResponse.error) {
				setPrompts(promptsResponse);
			}
		} catch (error) {
			console.error('Error fetching dashboard data:', error);
			showAlert('Failed to load dashboard data', 'danger');
		}
	};

	const handleTabClick = (tab) => {
		setActiveTab(tab);
		setEditingItem(null); // Reset editing state when changing tabs
	};

	const handleEdit = (item) => {
		setEditingItem(item);
	};

	const handleCreate = () => {
		setEditingItem(null);
	};

	const handleDelete = async (id, type) => {
		if (!window.confirm(`Are you sure you want to delete this ${type}?`)) {
			return;
		}

		try {
			let response;

			switch (type) {
				case 'admin':
					response = await adminAPI.deleteAdmin(id);
					if (!response.error) {
						setAdmins(admins.filter((admin) => admin.admin_id !== id));
						showAlert(
							response.message || 'Admin deleted successfully',
							'success'
						);
					}
					break;

				case 'teacher':
					response = await adminAPI.deleteTeacher(id);
					if (!response.error) {
						setTeachers(
							teachers.filter((teacher) => teacher.teacher_id !== id)
						);
						showAlert(
							response.message || 'Teacher deleted successfully',
							'success'
						);
					}
					break;

				case 'profile':
					response = await adminAPI.deleteModerationProfile(id);
					if (!response.error) {
						setModerationProfiles(
							moderationProfiles.filter((profile) => profile.id !== id)
						);
						showAlert(
							response.message || 'Moderation profile deleted successfully',
							'success'
						);
					}
					break;

				case 'prompt':
					response = await adminAPI.deletePrompt(id);
					if (!response.error) {
						setPrompts(prompts.filter((prompt) => prompt.id !== id));
						showAlert(
							response.message || 'Prompt deleted successfully',
							'success'
						);
					}
					break;

				default:
					break;
			}

			if (response && response.error) {
				showAlert(response.error, 'danger');
			}
		} catch (error) {
			console.error(`Error deleting ${type}:`, error);
			showAlert(`Failed to delete ${type}`, 'danger');
		}
	};

	const handleFormSubmit = async (formData, type) => {
		try {
			let response;

			switch (type) {
				case 'admin':
					if (editingItem) {
						response = await adminAPI.updateAdmin(
							editingItem.admin_id,
							formData
						);
					} else {
						response = await adminAPI.createAdmin(formData);
					}
					if (!response.error) {
						await fetchData();
						showAlert(
							response.message || 'Admin saved successfully',
							'success'
						);
					}
					break;

				case 'teacher':
					if (editingItem) {
						response = await adminAPI.updateTeacher(
							editingItem.teacher_id,
							formData
						);
					} else {
						response = await adminAPI.createTeacher(formData);
					}
					if (!response.error) {
						await fetchData();
						showAlert(
							response.message || 'Teacher saved successfully',
							'success'
						);
					}
					break;

				case 'profile':
					const profileFormData = new FormData();

					for (const key in formData) {
						if (key === 'documents') {
							if (formData[key]) {
								for (let i = 0; i < formData[key].length; i++) {
									profileFormData.append('documents', formData[key][i]);
								}
							}
						} else {
							profileFormData.append(key, formData[key]);
						}
					}

					if (editingItem) {
						response = await adminAPI.updateModerationProfile(
							editingItem.id,
							profileFormData
						);
					} else {
						response = await adminAPI.createModerationProfile(profileFormData);
					}
					if (!response.error) {
						await fetchData();
						showAlert(
							response.message || 'Moderation profile saved successfully',
							'success'
						);
					}
					break;

				case 'prompt':
					if (editingItem) {
						response = await adminAPI.updatePrompt(editingItem.id, formData);
					} else {
						response = await adminAPI.createPrompt(formData);
					}
					if (!response.error) {
						await fetchData();
						showAlert(
							response.message || 'Prompt saved successfully',
							'success'
						);
					}
					break;

				default:
					break;
			}

			if (response && response.error) {
				showAlert(response.error, 'danger');
				return false;
			}

			setEditingItem(null); // Reset editing state after successful submission
			return true;
		} catch (error) {
			console.error(`Error saving ${type}:`, error);
			showAlert(`Failed to save ${type}`, 'danger');
			return false;
		}
	};

	const handleDeleteFile = async (profileId, filename) => {
		try {
			const response = await adminAPI.deleteFile(profileId, filename);

			if (!response.error) {
				// Refresh the data to show updated file list
				await fetchData();
				showAlert(response.message || 'File deleted successfully', 'success');
			} else {
				showAlert(response.error, 'danger');
			}
		} catch (error) {
			console.error('Error deleting file:', error);
			showAlert('Failed to delete file', 'danger');
		}
	};

	return (
		<div>
			<Navbar />
			<Alert />
			<div className="ml-4">
				<h1 className="display-4 my-3">Admin Dashboard</h1>

				<ul className="nav nav-tabs">
					<li className="nav-item">
						<a
							className={`nav-link ${activeTab === 'admins' ? 'active' : ''}`}
							href="#"
							onClick={() => handleTabClick('admins')}
						>
							Admin
						</a>
					</li>
					<li className="nav-item">
						<a
							className={`nav-link ${activeTab === 'teachers' ? 'active' : ''}`}
							href="#"
							onClick={() => handleTabClick('teachers')}
						>
							Teacher
						</a>
					</li>
					<li className="nav-item">
						<a
							className={`nav-link ${
								activeTab === 'moderation-profiles' ? 'active' : ''
							}`}
							href="#"
							onClick={() => handleTabClick('moderation-profiles')}
						>
							Moderation Profile
						</a>
					</li>
					<li className="nav-item">
						<a
							className={`nav-link ${activeTab === 'prompts' ? 'active' : ''}`}
							href="#"
							onClick={() => handleTabClick('prompts')}
						>
							Prompts
						</a>
					</li>
				</ul>

				<div
					style={{ display: activeTab === 'admins' ? 'block' : 'none' }}
					id="admins"
				>
					<div className="flex-wrap">
						<table className="table table-bordered table-hover">
							<thead className="thead-dark">
								<tr>
									<th scope="col">ID</th>
									<th scope="col">Username</th>
									<th scope="col">Email</th>
									<th scope="col">Edit</th>
									<th scope="col">Delete</th>
								</tr>
							</thead>
							<tbody>
								{admins.map((admin) => (
									<tr key={admin.admin_id}>
										<td>{admin.admin_id}</td>
										<td className="text-break">{admin.username}</td>
										<td className="text-break">{admin.email}</td>
										<td>
											<button
												className="btn btn-sm btn-primary"
												onClick={() => handleEdit(admin)}
											>
												Edit
											</button>
										</td>
										<td>
											<button
												className="btn btn-sm btn-danger"
												onClick={() => handleDelete(admin.admin_id, 'admin')}
											>
												Delete
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>

						<AdminForm
							admin={editingItem}
							onSubmit={(data) => handleFormSubmit(data, 'admin')}
							onReset={handleCreate}
						/>
					</div>
				</div>

				<div
					style={{ display: activeTab === 'teachers' ? 'block' : 'none' }}
					id="teachers"
				>
					<div className="flex-wrap">
						<table className="table table-bordered table-hover">
							<thead className="thead-dark">
								<tr>
									<th>ID</th>
									<th>Username</th>
									<th>Email</th>
									<th>Subject</th>
									<th>Edit</th>
									<th>Delete</th>
								</tr>
							</thead>
							<tbody>
								{teachers.map((teacher) => (
									<tr key={teacher.teacher_id}>
										<td>{teacher.teacher_id}</td>
										<td className="text-break">{teacher.username}</td>
										<td className="text-break">{teacher.email}</td>
										<td className="text-break">{teacher.subject}</td>
										<td>
											<button
												className="btn btn-sm btn-primary"
												onClick={() => handleEdit(teacher)}
											>
												Edit
											</button>
										</td>
										<td>
											<button
												className="btn btn-sm btn-danger"
												onClick={() =>
													handleDelete(teacher.teacher_id, 'teacher')
												}
											>
												Delete
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>

						<TeacherForm
							teacher={editingItem}
							onSubmit={(data) => handleFormSubmit(data, 'teacher')}
							onReset={handleCreate}
						/>
					</div>
				</div>

				<div
					style={{
						display: activeTab === 'moderation-profiles' ? 'block' : 'none',
					}}
					id="moderation-profiles"
				>
					<div className="flex-wrap">
						<table
							className="table table-bordered table-hover"
							id="moderationTable"
						>
							<thead className="thead-dark">
								<tr>
									<th>ID</th>
									<th>Title</th>
									<th>View</th>
									<th>Delete</th>
								</tr>
							</thead>
							<tbody>
								{moderationProfiles.map((profile) => (
									<tr key={profile.id}>
										<td>{profile.id}</td>
										<td className="text-break">{profile.title}</td>
										<td>
											<button
												className="btn btn-sm btn-primary"
												onClick={() => handleEdit(profile)}
											>
												View/Edit
											</button>
										</td>
										<td>
											<button
												className="btn btn-sm btn-danger"
												onClick={() => handleDelete(profile.id, 'profile')}
											>
												Delete
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>

						<ModerationProfileForm
							profile={editingItem}
							models={models}
							onSubmit={(data) => handleFormSubmit(data, 'profile')}
							onReset={handleCreate}
							onDeleteFile={handleDeleteFile}
						/>
					</div>
				</div>

				<div
					style={{ display: activeTab === 'prompts' ? 'block' : 'none' }}
					id="prompts"
				>
					<div className="flex-wrap">
						<table className="table table-bordered table-hover">
							<thead className="thead-dark">
								<tr>
									<th>ID</th>
									<th>Condition</th>
									<th>View/Edit</th>
								</tr>
							</thead>
							<tbody>
								{prompts.map((prompt) => (
									<tr key={prompt.id}>
										<td>{prompt.id}</td>
										<td>{prompt.condition}</td>
										<td>
											<button
												className="btn btn-sm btn-primary"
												onClick={() => handleEdit(prompt)}
											>
												View/Edit
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>

						<PromptForm
							prompt={editingItem}
							onSubmit={(data) => handleFormSubmit(data, 'prompt')}
							onReset={handleCreate}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default AdminPage;
